define('services/qm/qualityAuth',[
    'lib/knockout',
    'lib/lodash4',
    'services/authService'
], function (ko,
             _,
             authService) {
    'use strict';

    //prefixes
    var PERM = "perm:";
    var ROLE = "role:";

    //domains
    var RECORDING_DOMAIN = PERM + "recording:";
    var QUALITY_DOMAIN = PERM + "quality:";
    var CONVERSATION_DOMAIN = PERM + "conversation:";
    var COACHING_DOMAIN = PERM + "coaching:";

    //entities
    var EVALUATION_ENTITY = QUALITY_DOMAIN + "evaluation:";
    var CALIBRATION_ENTITY = QUALITY_DOMAIN + "calibration:";
    var RECORDING_ENTITY = RECORDING_DOMAIN + "recording:";
    var ANNOTATION_ENTITY = RECORDING_DOMAIN + "annotation:";
    var ENCRYPTION_KEY_ENTITY = RECORDING_DOMAIN + "encryptionKey:";
    var RETENTION_POLICY_ENTITY = RECORDING_DOMAIN + "retentionPolicy:";
    var CROSS_RETENTION_POLICY_ENTITY = RECORDING_DOMAIN + "crossPlatformRetentionPolicy:";
    var EVALUATION_FORM_ENTITY = QUALITY_DOMAIN + "evaluationForm:";
    var SURVEY_FORM_ENTITY = QUALITY_DOMAIN + "surveyForm:";
    var ORPHAN_RECORDING_ENTITY = RECORDING_DOMAIN + "orphanRecording:";
    var SETTINGS_ENTITY = RECORDING_DOMAIN + "settings:";
    var SCREEN_RECORDING_ENTITY = RECORDING_DOMAIN + "screenRecording:";
    var KEYWORD_SET_ENTITY = QUALITY_DOMAIN + "keywordset:";
    var SURVEY_ENTITY = QUALITY_DOMAIN + "survey:";
    var CALL_ENTITY = CONVERSATION_DOMAIN + "call:";
    var DELETION_PROTECTION_ENTITY = RECORDING_DOMAIN + "deletionProtection:";
    var APPOINTMENT_ENTITY = COACHING_DOMAIN + "appointment:";

    //temporary role names
    var QUALITY_ADMIN_ROLE = ROLE + "qualityAdmin";
    var QUALITY_EVALUATOR_ROLE = ROLE + "qualityEvaluator";
    var AGENT = PERM + "routing:agent:onqueue";

    //temporary role lists
    var ADMIN_EVALUATOR = [QUALITY_ADMIN_ROLE, QUALITY_EVALUATOR_ROLE];
    var ADMIN_EVALUATOR_SUPERVISOR = [QUALITY_ADMIN_ROLE, QUALITY_EVALUATOR_ROLE];
    var ADMIN_EVALUATOR_AGENT = [QUALITY_ADMIN_ROLE, QUALITY_EVALUATOR_ROLE, AGENT];
    var ADMIN_EVALUATOR_SUPERVISOR_AGENT = [QUALITY_ADMIN_ROLE, AGENT, QUALITY_EVALUATOR_ROLE];

    //actions
    var VIEW = "view";
    var ADD = "add";
    var DELETE = "delete";
    var DOWNLOAD = "download";
    var VIEW_AUDIT = "viewAudit";
    var EDIT_RETENTION = "editRetention";
    var EDIT = "edit";
    var EDIT_SCORE = "editScore";
    var EDIT_AGENT_SIGNOFF = "editAgentSignoff";
    var RESTORE = "restore";
    var STOP = "stop";
    var EDIT_SCREEN_RECORDINGS = "editScreenRecordings";
    var EDIT_REGIONAL_STORAGE = "editRegionalStorage";
    var MONITOR = "monitor";
    var APPLY = "apply";
    var REVOKE = "revoke";

    function checkPermission(permissions, roles, divisionIds) {
        var ignoreDivisions = !divisionIds ? true : false;
        var authorizations = [].concat(permissions);

        if (divisionIds && divisionIds.length > 0) {
            authorizations = authorizations.concat(_.flatMap(permissions, function(permission) {
                return _.map(divisionIds, function(divisionId) {
                    return permission + ':' + divisionId;
                });
            }));
        }
        authorizations = authorizations.concat(roles);

        return authService.hasAuthorization({'any': authorizations}, ignoreDivisions);
    }

    var qualityAuthorization = {

        /**
         * Recordings
         */
        canApplyProtectionStatus: function(divisionIds) {
            return checkPermission([DELETION_PROTECTION_ENTITY + APPLY], [], divisionIds);
        },

        canRevokeProtectionStatus: function(divisionIds) {
            return checkPermission([DELETION_PROTECTION_ENTITY + REVOKE], [], divisionIds);
        },

        canViewRecording: function(divisionIds){
            return checkPermission([RECORDING_ENTITY + VIEW], ADMIN_EVALUATOR_SUPERVISOR_AGENT, divisionIds);
        },

        canDownloadRecording: function(divisionIds){
            return checkPermission([RECORDING_ENTITY + DOWNLOAD], [], divisionIds);
        },

        canViewRecordingAudit: function(divisionIds){
            return checkPermission([RECORDING_ENTITY + VIEW_AUDIT], ADMIN_EVALUATOR_SUPERVISOR, divisionIds);
        },

        canRestoreRecording: function(divisionIds){
            return checkPermission([RECORDING_ENTITY + RESTORE], ADMIN_EVALUATOR_SUPERVISOR_AGENT, divisionIds);
        },

        canDeleteRecording: function(divisionIds){
            return checkPermission([RECORDING_ENTITY + DELETE], ADMIN_EVALUATOR_SUPERVISOR, divisionIds);
        },

        canEditRetention: function(divisionIds){
            return checkPermission([RECORDING_ENTITY + EDIT_RETENTION], ADMIN_EVALUATOR_SUPERVISOR, divisionIds);
        },


        /**
         * Annotations
         */

        canViewAnnotation: function(divisionIds){
            return checkPermission([ANNOTATION_ENTITY + VIEW], ADMIN_EVALUATOR_SUPERVISOR_AGENT, divisionIds);
        },

        canAddAnnotation: function(divisionIds){
            return checkPermission([ANNOTATION_ENTITY + ADD], ADMIN_EVALUATOR_SUPERVISOR_AGENT, divisionIds);
        },

        canEditAnnotation: function(divisionIds){
            return checkPermission([ANNOTATION_ENTITY + EDIT], ADMIN_EVALUATOR_SUPERVISOR, divisionIds);
        },

        canDeleteAnnotation: function(divisionIds){
            return checkPermission([ANNOTATION_ENTITY + DELETE], ADMIN_EVALUATOR_SUPERVISOR_AGENT, divisionIds);
        },

        canViewAnnotationAudit: function(divisionIds){
            return checkPermission([ANNOTATION_ENTITY + VIEW_AUDIT], ADMIN_EVALUATOR_SUPERVISOR, divisionIds);
        },


        /**
         * Encryption Keys
         */

        canViewEncryptionKey: function(){
            return checkPermission([ENCRYPTION_KEY_ENTITY + VIEW], QUALITY_ADMIN_ROLE);
        },

        canEditEncryptionKey: function(){
            return checkPermission([ENCRYPTION_KEY_ENTITY + EDIT], QUALITY_ADMIN_ROLE);
        },

        /**
         * Evaluations
         */

        canAddEvaluation: function(divisionIds) {
            return checkPermission([EVALUATION_ENTITY + ADD], [QUALITY_ADMIN_ROLE], divisionIds);
        },

        canDeleteEvaluation: function(divisionIds) {
            return checkPermission([EVALUATION_ENTITY + DELETE], [QUALITY_ADMIN_ROLE], divisionIds);
        },

        canViewEvaluation: function(divisionIds){
            return checkPermission([EVALUATION_ENTITY + VIEW], ADMIN_EVALUATOR_AGENT, divisionIds);
        },

        canEditEvaluation: function(divisionIds){
            return checkPermission([EVALUATION_ENTITY + EDIT], [QUALITY_ADMIN_ROLE], divisionIds);
        },

        canEditScore: function(divisionIds){
            return checkPermission([EVALUATION_ENTITY + EDIT_SCORE], ADMIN_EVALUATOR, divisionIds);
        },

        canEditAgentSignoff: function(divisionIds){
            return checkPermission([EVALUATION_ENTITY + EDIT_AGENT_SIGNOFF], ADMIN_EVALUATOR_AGENT, divisionIds);
        },

        canViewEvaluationAudit: function(divisionIds){
            return checkPermission([EVALUATION_ENTITY + VIEW_AUDIT], ADMIN_EVALUATOR, divisionIds);
        },

        /**
         * Calibrations
         */
        canAddCalibration: function(){
            return checkPermission([CALIBRATION_ENTITY + ADD], [QUALITY_ADMIN_ROLE]);
        },

        canDeleteCalibration: function(){
            return checkPermission([CALIBRATION_ENTITY + DELETE], [QUALITY_ADMIN_ROLE]);
        },

        canViewCalibration: function(){
            return checkPermission([CALIBRATION_ENTITY + VIEW], [QUALITY_ADMIN_ROLE]);
        },

        canEditCalibration: function(){
            return checkPermission([CALIBRATION_ENTITY + EDIT], [QUALITY_ADMIN_ROLE]);
        },

        canViewCalibrationAudit: function(){
            return checkPermission([CALIBRATION_ENTITY + VIEW_AUDIT], [QUALITY_ADMIN_ROLE]);
        },

        /**
         * Audits
         */

        canViewAnyAudit: function(divisionIds){
            return checkPermission([
                RECORDING_ENTITY + VIEW_AUDIT,
                ANNOTATION_ENTITY + VIEW_AUDIT,
                EVALUATION_ENTITY + VIEW_AUDIT,
                SCREEN_RECORDING_ENTITY + VIEW_AUDIT
            ], ADMIN_EVALUATOR_SUPERVISOR, divisionIds);
        },

        /**
         * Retention Policies
         */

        canAddRetentionPolicy: function() {
        return checkPermission([RETENTION_POLICY_ENTITY + ADD, CROSS_RETENTION_POLICY_ENTITY + ADD], [QUALITY_ADMIN_ROLE]);
        },

        canDeleteRetentionPolicy: function() {
        return checkPermission([RETENTION_POLICY_ENTITY + DELETE, CROSS_RETENTION_POLICY_ENTITY + DELETE], [QUALITY_ADMIN_ROLE]);
        },

        canViewRetentionPolicy: function() {
        return checkPermission([RETENTION_POLICY_ENTITY + VIEW, CROSS_RETENTION_POLICY_ENTITY + VIEW], [QUALITY_ADMIN_ROLE]);
        },

        canEditRetentionPolicy: function() {
        return checkPermission([RETENTION_POLICY_ENTITY + EDIT, CROSS_RETENTION_POLICY_ENTITY + EDIT], [QUALITY_ADMIN_ROLE]);
        },

        canViewOrphanRecording: function () {
        return checkPermission([ORPHAN_RECORDING_ENTITY + VIEW], []);
        },

        canDeleteOrphanRecording: function () {
        return checkPermission([ORPHAN_RECORDING_ENTITY + DELETE], []);
        },

        canEditOrphanRecording: function() {
        return checkPermission([ORPHAN_RECORDING_ENTITY + EDIT], []);
        },

        /**
         * Survey Forms
         */

        canAddSurveyForm: function() {
            return checkPermission([SURVEY_FORM_ENTITY + ADD], []);
        },

        canDeleteSurveyForm: function() {
            return checkPermission([SURVEY_FORM_ENTITY + DELETE], []);
        },

        canViewSurveyForm: function() {
            return checkPermission([SURVEY_FORM_ENTITY + VIEW], []);
        },

        canEditSurveyForm: function() {
            return checkPermission([SURVEY_FORM_ENTITY + EDIT], []);
        },


        /**
         * Surveys
         */

        canViewSurvey: function (divisionIds) {
            return checkPermission([SURVEY_ENTITY + VIEW], [], divisionIds);
        },

        /**
         * Evaluation Forms
         */

        canAddEvaluationForm: function() {
            return checkPermission([EVALUATION_FORM_ENTITY + ADD], [QUALITY_ADMIN_ROLE]);
        },

        canDeleteEvaluationForm: function() {
        return checkPermission([EVALUATION_FORM_ENTITY + DELETE], [QUALITY_ADMIN_ROLE]);
        },

        canViewEvaluationForm: function() {
        return checkPermission([EVALUATION_FORM_ENTITY + VIEW], ADMIN_EVALUATOR_AGENT);
        },

        canEditEvaluationForm: function() {
        return checkPermission([EVALUATION_FORM_ENTITY + EDIT], [QUALITY_ADMIN_ROLE]);
        },

        /**
         * Screen Recordings
         */

        canViewScreenRecording: function(divisionIds) {
        return checkPermission([SCREEN_RECORDING_ENTITY + VIEW], [], divisionIds);
        },

        canStopScreenRecording: function(divisionIds) {
        return checkPermission([SCREEN_RECORDING_ENTITY + STOP], [], divisionIds);
        },

        canEditScreenRecordingRetention: function(divisionIds) {
        return checkPermission([SCREEN_RECORDING_ENTITY + EDIT_RETENTION], [], divisionIds);
        },

        canViewScreenRecordingAudits: function(divisionIds) {
        return checkPermission([SCREEN_RECORDING_ENTITY + VIEW_AUDIT], [], divisionIds);
        },

        canRestoreScreenRecordings: function (divisionIds) {
        return checkPermission([SCREEN_RECORDING_ENTITY + RESTORE], [], divisionIds);
        },

        /**
         * Keyword Sets
         */

        canViewKeywordSet: function(){
            return checkPermission([KEYWORD_SET_ENTITY + VIEW], []);
        },

        canAddKeywordSet: function(){
            return checkPermission([KEYWORD_SET_ENTITY + ADD], []);
        },

        canDeleteKeywordSet: function(){
            return checkPermission([KEYWORD_SET_ENTITY + DELETE], []);
        },

        canEditKeywordSet: function(){
            return checkPermission([KEYWORD_SET_ENTITY + EDIT], []);
        },

        /**
         * Recording Settings
         */

        canEditScreenRecordingSettings: function () {
            return checkPermission([SETTINGS_ENTITY + EDIT_SCREEN_RECORDINGS], []);
        },

        /**
        * Edit WEM-440 regional recording storage settings
        */

       canEditRegionalRecordingStorageSettings: function() {
           return checkPermission([SETTINGS_ENTITY + EDIT_REGIONAL_STORAGE], []);
       },

        /**
         * Call monitor
         */

        canMonitorCall: function (divisionIds) {
            return checkPermission([CALL_ENTITY + MONITOR], [], divisionIds);
        },

        /**
         * Schedule Coaching
         */
        canScheduleCoaching: function () {
            return checkPermission([APPOINTMENT_ENTITY + ADD], []);
        }
    };
    return qualityAuthorization;
});

